import "./PeopleAlsoTranslate.css";

interface TranslateSuggestion {
  id: string;
  title: string;
  eyebrow: string;
  answer: string;
  examples: Array<{ source: string; target: string }>;
}

const SUGGESTIONS: TranslateSuggestion[] = [
  {
    id: "yoruba",
    title: "AyeSearch Translate Yoruba",
    eyebrow: "Yoruba · English",
    answer:
      "AyeSearch Translate can use its verified Yoruba dictionary entries and canonical orthography to translate supported words and phrases between Yoruba and English.",
    examples: [
      { source: "bẹ́ẹ̀ni", target: "yes" },
      { source: "rárá", target: "no" },
      { source: "jọ̀wọ́", target: "please" },
    ],
  },
  {
    id: "hausa",
    title: "AyeSearch Translate Hausa",
    eyebrow: "Hausa · English",
    answer:
      "AyeSearch Translate supports verified Hausa dictionary concepts already stored in the AyeSearch translation source of truth.",
    examples: [
      { source: "eh", target: "yes" },
      { source: "ruwa", target: "water" },
      { source: "abinci", target: "food" },
    ],
  },
  {
    id: "swahili",
    title: "AyeSearch Translate Swahili",
    eyebrow: "Swahili · English",
    answer:
      "AyeSearch Translate can resolve supported Swahili words through AyeSearch's own dictionary concepts without requesting a generative AI answer.",
    examples: [
      { source: "ndiyo", target: "yes" },
      { source: "maji", target: "water" },
      { source: "chakula", target: "food" },
    ],
  },
  {
    id: "igbo",
    title: "AyeSearch Translate Igbo",
    eyebrow: "Igbo · English",
    answer:
      "AyeSearch Translate uses verified Igbo entries from the same internal dictionary source used by the translator card.",
    examples: [
      { source: "ee", target: "yes" },
      { source: "mmiri", target: "water" },
      { source: "nri", target: "food" },
    ],
  },
  {
    id: "english-yoruba",
    title: "AyeSearch Translate English to Yoruba",
    eyebrow: "English → Yoruba",
    answer:
      "For supported concepts, AyeSearch Translate maps the English dictionary concept to the canonical Yoruba entry, including Yoruba orthography where available.",
    examples: [
      { source: "yes", target: "bẹ́ẹ̀ni" },
      { source: "no", target: "rárá" },
      { source: "please", target: "jọ̀wọ́" },
    ],
  },
  {
    id: "english-hausa",
    title: "AyeSearch Translate English to Hausa",
    eyebrow: "English → Hausa",
    answer:
      "AyeSearch Translate can map supported English concepts directly to verified Hausa entries stored in AyeSearch.",
    examples: [
      { source: "water", target: "ruwa" },
      { source: "food", target: "abinci" },
      { source: "help", target: "taimako" },
    ],
  },
];

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function searchIcon(): string {
  return `
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M10.5 4a6.5 6.5 0 1 0 3.98 11.64l4.44 4.44 1.16-1.16-4.44-4.44A6.5 6.5 0 0 0 10.5 4Zm0 1.7a4.8 4.8 0 1 1 0 9.6 4.8 4.8 0 0 1 0-9.6Z"
      />
    </svg>
  `;
}

function answerHtml(item: TranslateSuggestion): string {
  return `
    <div class="people-translate-answer-copy">
      <div class="people-translate-powered">Powered by AyeSearch</div>
      <p>${escapeHtml(item.answer)}</p>
      <div class="people-translate-examples">
        ${item.examples
          .map(
            (example) => `
              <div class="people-translate-example">
                <span>${escapeHtml(example.source)}</span>
                <span class="people-translate-arrow">→</span>
                <strong>${escapeHtml(example.target)}</strong>
              </div>
            `,
          )
          .join("")}
      </div>
    </div>
  `;
}

export function renderPeopleAlsoTranslate(container: HTMLElement): void {
  container.innerHTML = `
    <section class="people-translate-section" aria-labelledby="people-translate-heading">
      <div class="people-translate-heading-row">
        <h2 id="people-translate-heading">People also translate</h2>
        <span>AyeSearch Translate</span>
      </div>

      <div class="people-translate-list">
        ${SUGGESTIONS.map(
          (item) => `
            <article class="people-translate-item" data-translate-item="${escapeHtml(item.id)}">
              <button
                class="people-translate-trigger"
                type="button"
                aria-expanded="false"
                aria-controls="people-translate-answer-${escapeHtml(item.id)}"
              >
                <span class="people-translate-trigger-copy">
                  <strong>${escapeHtml(item.title)}</strong>
                  <small>${escapeHtml(item.eyebrow)}</small>
                </span>

                <span class="people-translate-search-icon">
                  ${searchIcon()}
                </span>
              </button>

              <div
                class="people-translate-answer"
                id="people-translate-answer-${escapeHtml(item.id)}"
                hidden
              >
                ${answerHtml(item)}
              </div>
            </article>
          `,
        ).join("")}
      </div>
    </section>
  `;

  container
    .querySelectorAll<HTMLButtonElement>(".people-translate-trigger")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const item = button.closest<HTMLElement>(".people-translate-item");
        const answer = item?.querySelector<HTMLElement>(".people-translate-answer");
        if (!answer) return;

        const willOpen = answer.hidden;

        container
          .querySelectorAll<HTMLElement>(".people-translate-answer")
          .forEach((otherAnswer) => {
            otherAnswer.hidden = true;
          });

        container
          .querySelectorAll<HTMLButtonElement>(".people-translate-trigger")
          .forEach((otherButton) => {
            otherButton.setAttribute("aria-expanded", "false");
          });

        answer.hidden = !willOpen;
        button.setAttribute("aria-expanded", String(willOpen));
      });
    });
}
